// src/utils/studentScheduleUtils.js
// Shared utility for determining if a student is scheduled on a given date.
// Used by TodaysRoster and StaffSchedule to ensure consistent counts.

import { parseMMDDYYYY } from './dateUtils';

/**
 * Format a Date object to MM-DD-YY (care date format used in Firestore)
 */
export const formatDateMMDDYY = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${month}-${day}-${year}`;
};

/**
 * Returns true if student is a Bright Horizons student.
 * Requires BOTH billing type AND program selection to match —
 * prevents direct camp students with 'third-party' billing from being miscounted.
 */
export const isBHStudent = (student) => {
  const isBHBilling = student.billingType === 'bright-horizons' || student.billingType === 'third-party';
  const isBHProgram = student.programSelection?.toLowerCase().includes('bright horizons') ||
                      student.programSelection?.toLowerCase().includes('third-party billing');
  return isBHBilling && isBHProgram;
};

/**
 * Returns true if the student is a camp student — website camp signups
 * (program contains "camp" / "full-day") AND Bright Horizons students.
 */
export const isCampStudent = (student) => {
  const program = student.programSelection?.toLowerCase() || '';
  return isBHStudent(student) || program.includes('camp') || program.includes('full-day');
};

// Parse a camp date stored as either YYYY-MM-DD or MM-DD-YYYY
const parseCampDate = (str) => {
  if (!str) return null;
  const parts = str.split('-');
  if (parts[0].length === 4) return new Date(str + 'T00:00:00');
  return new Date(`${parts[2]}-${parts[0]}-${parts[1]}T00:00:00`);
};

/**
 * Returns true if the student is scheduled on the given date.
 * Mirrors the exact logic in TodaysRoster.jsx — single source of truth.
 * Does NOT filter onBreak — callers should apply that separately if needed.
 */
export const isScheduledForDate = (student, date) => {
  if (student.archived) return false;

  if (student.programStatus === 'inactive') {
    if (student.inactiveDate) {
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      const inactiveFrom = new Date(student.inactiveDate + 'T00:00:00');
      inactiveFrom.setHours(0, 0, 0, 0);
      if (checkDate >= inactiveFrom) return false;
    } else {
      return false;
    }
  }

  if (student.startDate) {
    const studentStart = parseMMDDYYYY(student.startDate);
    if (studentStart) {
      const checkDate = new Date(date);
      studentStart.setHours(0, 0, 0, 0);
      checkDate.setHours(0, 0, 0, 0);
      if (studentStart > checkDate) return false;
    }
  }

  const dateString = formatDateMMDDYY(date);
  const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });

  if (student.careDates && Array.isArray(student.careDates)) {
    const hasDate = student.careDates.some(care => care.date === dateString);
    if (hasDate) return true;
  }

  // Camp students with start/end date range but no matching careDates (weekdays only).
  // BH students attend ONLY via careDates, never via date range.
  // (Broader BH check than isBHStudent on purpose — matches TodaysRoster.)
  const looksBH = student.billingType === 'bright-horizons' || student.billingType === 'third-party' ||
                  (student.programSelection || '').toLowerCase().includes('bright horizons');
  if (student.campStartDate && student.campEndDate && !looksBH) {
    const campStart = parseCampDate(student.campStartDate);
    const campEnd = parseCampDate(student.campEndDate);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    if (campStart && campEnd && checkDate >= campStart && checkDate <= campEnd) {
      const dow = checkDate.getDay();
      if (dow !== 0 && dow !== 6) return true;
    }
  }

  if (student.programDaysPerWeek === '5 days' || student.programDaysPerWeek === '5') {
    if (weekday !== 'Saturday' && weekday !== 'Sunday') return true;
  }

  if (student.pickupDays) {
    const days = Array.isArray(student.pickupDays)
      ? student.pickupDays
      : student.pickupDays.split(/[,\n]+/).map(d => d.trim()).filter(d => d);
    return days.includes(weekday);
  }

  return false;
};

/**
 * Returns true if the student is a camp student (website or Bright Horizons)
 * attending on the given date. Shared by StaffSchedule and TodaysRoster.
 * Does NOT filter onBreak — callers should apply that separately if needed.
 */
export const isCampStudentOnDate = (student, date) =>
  isCampStudent(student) && isScheduledForDate(student, date);

/**
 * Build { studentId: Date } of each student's earliest actual attendance
 * (checked-in / checked-out records only — 'absent' / 'pending' don't count).
 * Build once per render and pass to isNewStudentOnDate.
 */
export const buildFirstAttendanceMap = (attendanceRecords = []) => {
  const firstByStudent = {};
  attendanceRecords.forEach(r => {
    if (r.status !== 'checked-in' && r.status !== 'checked-out') return;
    const d = parseMMDDYYYY(r.date);
    if (!d) return;
    d.setHours(0, 0, 0, 0);
    if (!firstByStudent[r.studentId] || d < firstByStudent[r.studentId]) firstByStudent[r.studentId] = d;
  });
  return firstByStudent;
};

/**
 * A student's first day at the center: earliest of first actual attendance,
 * first camp care date, and enrollment startDate. Null if none known.
 */
export const getFirstDayAtCenter = (student, firstAttendanceMap = {}) => {
  const candidates = [firstAttendanceMap[student.id]];
  (student.careDates || []).forEach(c => candidates.push(parseMMDDYYYY(c.date)));
  if (student.startDate) candidates.push(parseMMDDYYYY(student.startDate));

  return candidates
    .filter(Boolean)
    .map(d => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; })
    .reduce((earliest, d) => (!earliest || d < earliest ? d : earliest), null);
};

/**
 * True only on the student's first-ever day at the center (or if they have no
 * attendance, care dates, or start date at all — they've never been here).
 */
export const isNewStudentOnDate = (student, date, firstAttendanceMap = {}) => {
  const firstDay = getFirstDayAtCenter(student, firstAttendanceMap);
  if (!firstDay) return true;
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate <= firstDay;
};

/**
 * Get student breakdown stats for a given date — same categories as TodaysRoster stat bar.
 */
export const getDayStudentStats = (allStudents, date) => {
  const dateISO = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;

  const dayStudents = allStudents.filter(s => {
    if (!isScheduledForDate(s, date)) return false;
    if (s.onBreak === true) {
      const breakStarted = !s.breakStartDate || dateISO >= s.breakStartDate;
      const breakNotEnded = !s.breakEndDate || dateISO <= s.breakEndDate;
      if (breakStarted && breakNotEnded) return false;
      if (!s.breakEndDate) return false;
    }
    return true;
  });

  // dayStudents is already date- and break-filtered, so isCampStudentOnDate's date check always passes here
  const camp       = dayStudents.filter(s => isCampStudentOnDate(s, date));
  const bhCamp     = dayStudents.filter(s => isBHStudent(s));
  const pickup     = dayStudents.filter(s => s.needsPickup);
  const dropOff    = dayStudents.filter(s =>
    !s.needsPickup &&
    s.programSelection?.toLowerCase().includes('after school')
  );
  const directCamp = dayStudents.filter(s =>
    (s.programSelection?.toLowerCase().includes('camp') ||
     s.programSelection?.toLowerCase().includes('full-day')) &&
    !isBHStudent(s)
  );
  const other      = dayStudents.filter(s =>
    !s.programSelection?.toLowerCase().includes('after school') &&
    !s.programSelection?.toLowerCase().includes('camp') &&
    !s.programSelection?.toLowerCase().includes('full-day') &&
    !isBHStudent(s)
  );

  return {
    total: dayStudents.length,
    camp: camp.length,   // all camp students: website + Bright Horizons
    bhCamp: bhCamp.length,
    pickup: pickup.length,
    dropOff: dropOff.length,
    directCamp: directCamp.length,
    other: other.length,
  };
};